import { Button, Callout, Input, useToast } from '@fruuit/ui'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router'
import { errorMessage, isApiError, safeNext } from '../api/client'
import { useApiMutation } from '../api/hooks'
import { meQueryKey, requestSignInCode, verifySignInCode } from '../api/session'
import { useTitle } from '../lib/useTitle'
import { AuthHeading, AuthLayout } from './AuthLayout'

/**
 * Code entry for email sign-in (A-04, ADR-D08). `method=email`: the emailed
 * 6-digit code (15 minutes, 5 attempts, resend after 60 s). `method=authenticator`:
 * the user has an authenticator app, nothing was emailed, and the code comes
 * from the app or is one of their recovery codes.
 */
export function SignInCodePage() {
  const [params] = useSearchParams()
  const email = params.get('email') ?? ''
  const authenticator = params.get('method') === 'authenticator'
  useTitle(authenticator ? 'Enter your authenticator code' : 'Enter your code')
  const next = safeNext(params.get('next'))
  const [code, setCode] = useState('')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const verify = useApiMutation(verifySignInCode, {
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: meQueryKey })
      navigate(safeNext(result?.next) ?? next ?? '/', { replace: true })
    },
    onError: (error) => {
      if (isApiError(error, 'no_access', 'org_suspended')) {
        navigate(error.code === 'org_suspended' ? '/no-access?reason=suspended' : '/no-access', {
          replace: true,
        })
      }
    },
  })
  const resend = useApiMutation(requestSignInCode, {
    onSuccess: () => toast({ message: 'A new code is on its way. Codes expire after 15 minutes.' }),
    onError: (error) => toast({ tone: 'error', message: errorMessage(error) }),
  })

  if (!email) return <Navigate to="/signin" replace />

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    verify.mutate({ email, code: code.trim(), ...(next ? { next } : {}) })
  }

  const backTo = next ? `/signin?next=${encodeURIComponent(next)}` : '/signin'
  const locked = isApiError(verify.error, 'code_locked')

  return (
    <AuthLayout>
      {authenticator ? (
        <AuthHeading title="Open your authenticator app">
          Enter the 6-digit code it shows for{' '}
          <strong className="font-semibold text-ink">{email}</strong>. No email was sent. If you
          have lost the app, enter one of your recovery codes instead.
        </AuthHeading>
      ) : (
        <AuthHeading title="Check your email">
          If <strong className="font-semibold text-ink">{email}</strong> has access, a 6-digit code
          is on its way. It expires in 15 minutes.
        </AuthHeading>
      )}
      {locked ? (
        <Callout tone="danger" role="alert" className="mb-6">
          {errorMessage(verify.error)} For your security, sign-in by code is paused for this
          address.
        </Callout>
      ) : null}
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        {authenticator ? (
          <Input
            label="Authenticator code"
            name="code"
            autoComplete="one-time-code"
            autoCapitalize="characters"
            maxLength={11}
            required
            value={code}
            onChange={(event) =>
              setCode(event.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))
            }
            inputClassName="text-center font-mono text-xl tracking-[0.3em]"
            hint="6 digits from the app, or a recovery code such as ABCDE-FGHJK."
            error={verify.error && !locked ? errorMessage(verify.error) : undefined}
          />
        ) : (
          <Input
            label="Sign-in code"
            name="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            inputClassName="text-center font-mono text-xl tracking-[0.5em]"
            error={verify.error && !locked ? errorMessage(verify.error) : undefined}
          />
        )}
        <Button type="submit" size="lg" fullWidth loading={verify.isPending} disabled={locked}>
          Sign in
        </Button>
      </form>
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-[13px]">
        <Link to={backTo} className="inline-flex items-center gap-1.5 font-medium text-teal">
          <ArrowLeft aria-hidden="true" className="size-4" />
          Use a different email
        </Link>
        {authenticator ? null : (
          <Button
            variant="ghost"
            size="sm"
            loading={resend.isPending}
            onClick={() => resend.mutate({ email })}
          >
            Send a new code
          </Button>
        )}
      </div>
    </AuthLayout>
  )
}
